// src/components/JobDetailSection.tsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
} from "@mui/material";
import { Job, JobDetail } from "../types/job";
import { fetchJobDetails } from "../Services/jobService"; // Assume this function is implemented to call your API

interface JobDetailSectionProps {
  job: Job;
}

const JobDetailSection: React.FC<JobDetailSectionProps> = ({ job }) => {
  const [expanded, setExpanded] = useState(false);
  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExpand = async () => {
    // Toggle collapse if already expanded
    if (expanded) {
      setExpanded(false);
      return;
    }

    // Otherwise, if not yet loaded then fetch details
    if (!jobDetail) {
      setLoading(true);
      try {
        const data: JobDetail = await fetchJobDetails(job.id);
        setJobDetail(data);
        setExpanded(true);
      } catch (err) {
        setError("Failed to fetch job details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    } else {
      setExpanded(true);
    }
  };

  return (
    <Paper elevation={3} sx={{ mt: 2, p: 2 }}>
      {/* Basic Job Information */}
      <Typography variant="h6">{job.title}</Typography>
      {job.deadline && (
        <Typography variant="body2" color="textSecondary">
          Deadline: {new Date(job.deadline).toLocaleDateString()}
        </Typography>
      )}
      {job.deadline && (
        <Typography variant="body1" sx={{ mt: 1 }}>
          {job.deadline}
        </Typography>
      )}
      
      <Button variant="contained" color="primary" onClick={handleExpand} sx={{ mt: 2 }}>
        {expanded ? "Collapse" : "Expand"}
      </Button>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {expanded && jobDetail && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Job Description:</Typography>
          <Typography variant="body2">{jobDetail.jobDescription}</Typography>

          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Location:
          </Typography>
          <Typography variant="body2">{jobDetail.location}</Typography>

          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Screening Questions:
          </Typography>
          {jobDetail.screeningQuestions.length > 0 ? (
            <List>
              {jobDetail.screeningQuestions.map((q, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemText primary={`${index + 1}. ${q}`} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No screening questions provided.</Typography>
          )}

          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Candidate Applications:
          </Typography>
          {jobDetail.candidateApplications.length > 0 ? (
            <List>
              {jobDetail.candidateApplications.map((candidate) => (
                <ListItem key={candidate.id} divider>
                  <ListItemText 
                    primary={candidate.name} 
                    secondary={`Email: ${candidate.email || "N/A"} | Submitted: ${new Date(candidate.submittedDate).toLocaleDateString()}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No candidate applications available.</Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default JobDetailSection;
